import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// eslint-disable-next-line spire/fenced-imports
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: FooterLinkListStyles;
}

export interface FooterLinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    footerLink?: InjectableCss;
}

const isMGBW = isThemeActive(THEME_IDS.MGBW);

export const footerLinkListStyles: FooterLinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 33px 0 32px;
            ${isMGBW && css`
                padding: 0;
                flex: 0 0 auto;
                margin-bottom: 40px;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    isMGBW ? null : css`
                        padding-bottom: 16px;
                    `,
                    isMGBW ? null : css`
                        padding-bottom: 16px;
                    `,
                ])}
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) ? "20px" : "10px"} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 9px !important;
                    `,
                    css`
                        margin-bottom: 9px !important;
                    `,
                    css`
                        margin-bottom: 9px !important;
                    `,
                    null,
                    null,
                ])}
            span[class^="TypographyStyle"] {
                opacity: 1;
                text-align: left;
                font-family: ${TYPOGRAPHY.footerHeadings.fontFamily};
                font-weight: ${TYPOGRAPHY.footerHeadings.weight};
                font-size: ${TYPOGRAPHY.footerHeadings.size};
                line-height: ${TYPOGRAPHY.footerHeadings.lineHeight};
                color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                cursor: default;
                text-transform: ${TYPOGRAPHY.footerHeadings.textTransform};
                font-style: ${TYPOGRAPHY.footerHeadings.fontStyle};
                ${isMGBW ? `letter-spacing: ${(TYPOGRAPHY.footerHeadings as any).letterSpacing};` : ""}
            }

            span.list-title:hover {
                span {
                    color: ${isMGBW ? COLORS.text.accent : COLORS.text.white};
                }
            }
        `,
    },
    title: {
        css: css`
            ${isThemeActive(THEME_IDS.GLOBAL_VIEWS)
                ? 
                    `margin-bottom: 10px;`
                : 
                    `margin-bottom: 20px;`
            }
            > span {
                opacity: 1;
                text-align: left;
                text-transform: uppercase;
            }
        `,
    },
    linkWrapper: {
        css: css`
            ${isMGBW ? css`
                padding-top: ${ELEMENTS.footerLinks.marginBottom};
                margin-bottom: 0;
            ` : css`
                margin-bottom: ${ELEMENTS.footerLinks.marginBottom};
            `}

            span[class^="TypographyStyle"] {
                text-transform: ${TYPOGRAPHY.footerLinks.textTransform};
                color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.base} !important;
                font-size: ${isMGBW ? TYPOGRAPHY.body.size : "14px"} !important;
                letter-spacing: ${TYPOGRAPHY.footerLinks.letterSpacing};
                ${isMGBW ? "line-height: 1.5;" : ""}
                /* ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-family: Roboto !important;
                            line-height: 23px !important;
                            font-size: 15px !important;
                        `,
                        css`
                            font-family: Roboto !important;
                            line-height: 23px !important;
                            font-size: 15px !important;
                        `,
                        css`
                            font-family: Roboto !important;
                            line-height: 23px !important;
                            font-size: 15px !important;
                        `,
                        null,
                        null,
                    ])} */
            }
        `,
    },
    footerLink: {
        css: css`
            span[class^="TypographyStyle"] {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${isMGBW ? TYPOGRAPHY.weight.base : TYPOGRAPHY.weight.bold};
                font-size: ${isMGBW ? TYPOGRAPHY.body.size : TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                letter-spacing: ${TYPOGRAPHY.footerLinks.letterSpacing};
                &:hover {
                    color: ${isMGBW ? COLORS.text.accent : "#a29d92"};
                }
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {

}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const FooterLinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination).links;
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", footerLinkListStyles, extendedStyles);

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${styles.linkListWrapper?.css}
            &::after {
                content: "";
                position: absolute;
                width: 0.5px;
                height: 200px;
                top: 10px;
                display: block;
                opacity: 0.2;
                right: 0;
                margin: 0 35px;
            }
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
            ${styles.linkWrapper?.css}
        `,
    };

    const customCssWrapper = {
        css: css`
            display: flex;
            ${fields.customCSS}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {titleLink ? (
                        <Link className="list-title" href={titleLink.url} role="button">
                            {fields.title || titleLink.title}
                        </Link>
                    ) : (
                        <Typography className="list-title" as="h3" {...styles.title} role="button">
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
                {links.map((link, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper className="links-wrapper" key={index} {...directionStyles}>
                        {link.url && (
                            <Link
                                className="list-link"
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...styles.footerLink}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: FooterLinkList,
    definition: {
        group: "Footer",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
